import React, { useState, useEffect, useCallback, useMemo, useRef, useContext } from 'react';
import IndiaDataGrid from "../../include/IndiaDataGrid";
import { Box, Button } from '@mui/material';
import { GridToolbarColumnsButton, GridToolbarExportContainer, GridToolbarQuickFilter } from '@mui/x-data-grid';

import { callPostApi } from '../../function/ApiCallFunction';
import CustomFooterWithLoadMore from '../../include/footerWithLoadMore';

import { Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import PrintReviewQr from '../../components/modal/PrintReviewQr';
import { DataContext } from '../../context/DataContext';

const ClubReviewList = () => 
{
    const navigate = useNavigate();

    const {loginData }  = useContext(DataContext);
    const store_id      = loginData?.store_info?.store_id;
    const store_name    = loginData?.store_info?.store_name;
    const store_image   = loginData?.store_info?.store_image || "";
    const qrLink        = `${window.location.origin}/reviewadd?id=${store_id}`;

    const [allData,        setAllData]        = useState([]);
    const [loading,        setLoading]        = useState(false);
    const [pageIndex,      setPageIndex]      = useState(0);
    const [hasMoreData,    setHasMoreData]    = useState(true);
    const [filtersApplied, setFiltersApplied] = useState(false);
    const [filtersLoaded,  setFiltersLoaded]  = useState(false);
    
    const isInitialSessionLoad = useRef(false);
    const hasFetchedRef        = useRef(false);

    /* ------------------ DATE HELPER ------------------ */
    const getDateNDaysAgo = (n) => {
        const date = new Date();
        date.setDate(date.getDate() - n);
        const year = date.getFullYear();
        const month = (`0${date.getMonth() + 1}`).slice(-2);
        const day = (`0${date.getDate()}`).slice(-2);
        return `${year}-${month}-${day}`;
    };

    // Default Filter For Searching
    const defaultFilters = useMemo(() => ({store_id,searchKey: '', startDate:getDateNDaysAgo(0), endDate:getDateNDaysAgo(0), fetchLimit: '10', pageSize: '10' }), [store_id]);
    const [filters, setFilters] = useState(defaultFilters);
    
    // Reset Data States
    const resetDataStates = () => {
        setAllData([]);
        setPageIndex(0);
        setHasMoreData(true);
        setLoading(false);
    };

    // Apply Filters
    const applyFilters = () => 
    {
        setAllData([]);
        setPageIndex(0);
        setHasMoreData(true);
        setFiltersApplied(true);
    };

    // Handle Enter Key
    const handleEnterApplyFilters = (e) => { if (e.key === 'Enter') applyFilters(); };

    // Clear Filters
    const clearFilters = () => 
    {
        setFilters(defaultFilters);
        setAllData([]);
        setPageIndex(0);
        setHasMoreData(true);
        sessionStorage.setItem('reviewListFilter', JSON.stringify(defaultFilters));
        setFiltersApplied(true);
        fetchData();
    };

    // Load from sessionStorage
    useEffect(() => 
    {
        const storedState = sessionStorage.getItem('reviewListFilter');
        if (storedState) 
        {
            try 
            {
                const parsedState = JSON.parse(storedState);
                setFilters({ ...defaultFilters, ...parsedState });
                isInitialSessionLoad.current = true;
                setFiltersApplied(true);
            } 
            catch (error) 
            {
                console.error('Failed to parse stored filters:', error);
                sessionStorage.removeItem('reviewListFilter');
            }
        }
    }, [defaultFilters]);

    // Fetch Data Function
    const fetchData = useCallback(async (page = pageIndex) => 
    {
        if (!filtersLoaded || loading || !hasMoreData) return;

        setLoading(true);
        try 
        {
            const StartNum = page * filters.fetchLimit;
            const payload = {mod: "STORE_REVIEW_LIST", data_arr: {...filters,start: StartNum,limit: filters.fetchLimit,}};
            
            const response = await callPostApi('store-review', payload);

            if (response.status === '200' && response.data) 
            {
                const apiResponse = response.data.success || [];
                setAllData(prev => [...prev, ...apiResponse]);
                setPageIndex(page);

                if (apiResponse.length < filters.fetchLimit) { setHasMoreData(false); }
            } 
            else 
            {
                resetDataStates();
                setHasMoreData(false);
            }
        } 
        catch (error) { resetDataStates(); } 
        finally { setLoading(false); }

    }, [filters, filtersLoaded, loading, hasMoreData, pageIndex]);

    // Initial fetch trigger
    useEffect(() => {
        if (!hasFetchedRef.current && filtersLoaded) {
            hasFetchedRef.current = true;
            fetchData();
        }
    }, [filtersLoaded, fetchData]);

        // Save to sessionStorage
    useEffect(() => {
        if (filtersApplied) 
        {
            sessionStorage.setItem('reviewListFilter', JSON.stringify(filters));
            if (!isInitialSessionLoad.current) { fetchData(0); }
            isInitialSessionLoad.current = false;
            setFiltersApplied(false);
        }
    }, [filtersApplied, filters, fetchData]);

    // Set filters loaded once
    useEffect(() => { if (!filtersLoaded) setFiltersLoaded(true); }, [filtersLoaded]);

    // Custom Toolbar
    const CustomToolbar = () => {
        return (
            <GridToolbarExportContainer sx={{ justifyContent: 'space-between', px: 2, py: 1 }}>
                <GridToolbarColumnsButton /><GridToolbarQuickFilter debounceMs={300} />
            </GridToolbarExportContainer>
        );
    };

    const fetchLimits    = [ "5", "10", "20", "50", "100", "250", "500", "1000"];

    return (
    <div>
        <Container maxWidth="xl" sx={{ my: 2 }}>

        {/* HEADER BUTTONS */}
        <Box display="flex" justifyContent="flex-start" alignItems="center" flexWrap="wrap" gap={2} mt={2} mb={2}>

            <PrintReviewQr qrValue={qrLink} store_name={store_name} store_image={store_image}  />
            
            <Box display="flex" gap={2} ml="auto">
                <Button sx={{background: "linear-gradient(195deg, #42424a, #191919)",color: "#ffffff",
                        boxShadow:"rgba(0, 0, 0, 0.02) 0px 1px 3px 0px, rgba(27, 31, 35, 0.15) 0px 0px 0px 1px", }}onClick={clearFilters}>Clear Filters</Button>

                <Button variant="contained" sx={{ background: "#1976d2" }} onClick={() => navigate(-1) } > Back </Button>
            </Box>

        </Box>

        <div className='mb-3'>
                       
            {/* Filter Inputs and Apply Filters button */}
            <div className="row align-items-center g-2 searchFilter">

                {/* Search flter */}
                <div className="col-12 col-md-2">
                    <input type="text" className="form-control" placeholder="Name / Mobile" value={filters.searchKey}
                        onChange={(e) => setFilters(prev => ({ ...prev, searchKey: e.target.value }))} onKeyDown={handleEnterApplyFilters} />
                </div>

                {/* Party Date */}
                <div className="col-12 col-md-2">
                    <input type="date" className="form-control" placeholder="Start Date" value={filters.startDate}
                        onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))} />
                </div>

                <div className="col-12 col-md-2">
                    <input type="date" className="form-control" placeholder="End Date" value={filters.endDate}
                        onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))} />
                </div>

                {/* Page Size Filter */}  
                <div className="col-12 col-md-2">
                    <select className="form-control" value={filters.fetchLimit} onChange={(e) => setFilters(prev => ({ ...prev, fetchLimit: e.target.value }))} >
                        <option value="">Record Fetch Limit</option>
                        {fetchLimits.map(value => ( <option key={value} value={value}>{value}</option>))}
                    </select>
                </div>     

              
                {/* Apply flter */}
                <div className="col-12 col-md-2 ms-auto">                        
                    <Button  sx={{background:'linear-gradient(195deg, #49a3f1, #1A73E8)', color:'#ffffff',ml:1,
                        boxShadow: 'rgba(0, 0, 0, 0.02) 0px 1px 3px 0px, rgba(27, 31, 35, 0.15) 0px 0px 0px 1px'}}
                    style={{float: 'right'}} onClick={() => { resetDataStates(); fetchData(0); applyFilters(); }} >Apply Filters</Button>
                    
                </div>
                

                
            </div>
        </div>
        
        <Box sx={{ height: 'auto', width: '100%' }}>
            <IndiaDataGrid rows={allData.map((row, i) => ({...row,srNo: i + 1 ,id: row.row_temp_id || `${pageIndex}-${i}`}))}
                columns={[
                    { field: 'srNo',            headerName: 'Sr. No.',    flex: 0.4, sortable: false,filterable: false,},
                    { field: 'name',            headerName: 'Name',       flex: 1, },
                    { field: 'mobile',          headerName: 'Mobile',     flex: 1, },
                    { field: 'email',           headerName: 'Email',      flex: 1, },
                    { field: 'shopping_rating', headerName: 'Shopping',   flex: 1, },
                    { field: 'product_rating',  headerName: 'Product',    flex: 1, },
                    { field: 'staff_rating',    headerName: 'Staff',      flex: 1, },
                    { field: 'comment',         headerName: 'Comment',    flex: 1, },
                    { field: 'date_added',      headerName: 'Date Added', flex: 1, align: 'right', headerAlign: 'right', },                                 
                ]}

                localeText={{ noRowsLabel: "No records found", noResultsOverlayLabel: "No matching data found", }}
                pageSize={Number(filters.pageSize)}
                onPageSizeChange={(newSize) => setFilters((prev) => ({ ...prev, pageSize: String(newSize) })) }
                pageSizeOptions={[10, 15, 20, 25, 40, 50, 80, 100]}
                pagination
                
                disableSelectionOnClick
                initialState={{pagination: {paginationModel: {pageSize: Number(filters.pageSize),page: 0,},},
                    columns: {columnVisibilityModel: {  },},
                    }}
                slots={{toolbar: CustomToolbar, footer: () => ( <CustomFooterWithLoadMore onLoadMore={() => fetchData(pageIndex + 1)} hasMoreData={hasMoreData} loading={loading} /> ), }}
                slotProps={{toolbar: {showQuickFilter: true,quickFilterProps: { debounceMs: 300 },},}}
                sx={{ '& .MuiDataGrid-cell': {fontSize: '13px', }, 
                    '& .MuiDataGrid-columnHeaderTitle': {fontSize: '15px',fontWeight: 'bold', whiteSpace: 'normal !important', 
                            lineHeight: '1.5', textAlign: 'center',},
                        "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {outline: "none"} }}
                rowHeight={38} headerHeight={45}
                
            />
        </Box>
    </Container>

    </div>
  );
};

export default ClubReviewList;